import { useEffect, useState } from "react";
import { X, Send, Search, ChevronRight, Loader, CheckCircle, FileText } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";

// Detect {{N}} placeholders in text and return count
const countParams = (text = "") => {
  const matches = text.match(/\{\{\d+\}\}/g) || [];
  const indices = new Set(matches.map((m) => parseInt(m.replace(/\D/g, ""))));
  return indices.size;
};

// Extract highest param index
const getParamCount = (bodyText = "") => countParams(bodyText);

const CATEGORY_COLORS = {
  MARKETING: "bg-purple-100 text-purple-700",
  UTILITY: "bg-blue-100 text-blue-700",
  AUTHENTICATION: "bg-orange-100 text-orange-700",
};

const categoryColor = (cat = "") =>
  CATEGORY_COLORS[cat.toUpperCase()] || "bg-gray-100 text-gray-600";

const TemplatePicker = ({ convId, onClose, onSent }) => {
  const [templates, setTemplates]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [selected, setSelected]         = useState(null);   // template object
  const [metaBody, setMetaBody]         = useState(null);   // body text from Meta
  const [metaLoading, setMetaLoading]   = useState(false);
  const [params, setParams]             = useState([]);     // body param inputs
  const [sending, setSending]           = useState(false);

  // Fetch approved templates from local DB
  useEffect(() => {
    api.get("/getTemplate")
      .then((r) => {
        const approved = (r.data.templates || []).filter(
          (t) => t.status?.toLowerCase() === "approved"
        );
        setTemplates(approved);
      })
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
  }, []);

  // When a template is selected, fetch body text from Meta for preview
  const selectTemplate = async (tpl) => {
    setSelected(tpl);
    setParams([]);
    setMetaBody(null);
    setMetaLoading(true);
    try {
      const r = await api.get(`/message-templates/meta/${encodeURIComponent(tpl.templateName)}`);
      const bodyComp = r.data.template?.components?.find((c) => c.type === "BODY");
      const bodyText = bodyComp?.text || null;
      setMetaBody(bodyText);
      const n = getParamCount(bodyText || "");
      setParams(Array.from({ length: n }, () => ""));
    } catch {
      // Meta fetch failed — still allow sending without body preview
      setMetaBody(null);
    } finally {
      setMetaLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selected || sending) return;
    setSending(true);
    try {
      // Send the rendered preview text so the backend can store it for display
      const renderedBody = metaBody
        ? params.reduce(
            (text, val, i) => text.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, "g"), val || `{{${i + 1}}}`),
            metaBody
          )
        : null;
      await api.post(`/conversations/${convId}/send-template`, {
        templateId: selected.id,
        bodyParams: params.filter(Boolean),
        bodyText: renderedBody,
      });
      toast.success(`Template "${selected.templateName}" sent!`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to send template");
    } finally {
      setSending(false);
    }
  };

  const filtered = templates.filter((t) =>
    t.templateName.toLowerCase().includes(search.toLowerCase())
  );

  // Build full URL for media header preview
  const BASE_URL = process.env.REACT_APP_BASE_URL || "";
  const headerMediaUrl =
    selected?.headerValue && ["image", "video", "document"].includes(selected?.headerType)
      ? `${BASE_URL}${selected.headerValue}`
      : null;

  // Fill preview text with param values
  const previewText = metaBody
    ? params.reduce(
        (text, val, i) => text.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, "g"), val || `{{${i + 1}}}`),
        metaBody
      )
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-0 sm:px-4">
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: "90dvh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Send Template</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Template list */}
          <div className={`flex flex-col border-r border-gray-100 ${selected ? "hidden sm:flex sm:w-52" : "flex w-full sm:w-52"}`}>
            {/* Search */}
            <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
                <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder-gray-400"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-10 px-3">
                  {templates.length === 0 ? "No approved templates." : "No matches."}
                </p>
              ) : (
                filtered.map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => selectTemplate(tpl)}
                    className={`w-full text-left px-3 py-2.5 border-b border-gray-50 transition-colors flex items-center gap-2 ${
                      selected?.id === tpl.id
                        ? "bg-blue-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{tpl.templateName}</p>
                      <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mt-0.5 ${categoryColor(tpl.category)}`}>
                        {tpl.category}
                      </span>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Detail / params panel */}
          {selected ? (
            <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
              {/* Back button on mobile */}
              <button
                onClick={() => setSelected(null)}
                className="sm:hidden flex items-center gap-1 text-xs text-blue-600 px-3 py-2 border-b border-gray-100 flex-shrink-0"
              >
                ← Back to list
              </button>

              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                {/* Template info */}
                <div>
                  <p className="text-sm font-semibold text-gray-900">{selected.templateName}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${categoryColor(selected.category)}`}>
                      {selected.category}
                    </span>
                    <span className="text-[10px] text-gray-400">{selected.language}</span>
                    {selected.headerType !== "none" && (
                      <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded capitalize">
                        {selected.headerType} header
                      </span>
                    )}
                  </div>
                </div>

                {/* Header media preview */}
                {headerMediaUrl && (
                  <div>
                    {selected.headerType === "image" && (
                      <img
                        src={headerMediaUrl}
                        alt="header"
                        className="w-full rounded-xl object-cover max-h-40"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    {selected.headerType === "video" && (
                      <video
                        src={headerMediaUrl}
                        className="w-full rounded-xl max-h-40 object-cover bg-black"
                        controls={false}
                        muted
                        preload="metadata"
                      />
                    )}
                    {selected.headerType === "document" && (
                      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                        <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        <span className="text-xs text-gray-600 truncate">{selected.headerValue?.split("/").pop()}</span>
                      </div>
                    )}
                  </div>
                )}
                {selected.headerType === "text" && selected.headerValue && (
                  <p className="text-sm font-semibold text-gray-800">{selected.headerValue}</p>
                )}

                {/* Body preview */}
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1.5">Message Preview</p>
                  <div className="bg-green-50 border border-green-100 rounded-xl px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap min-h-[56px]">
                    {metaLoading ? (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Loader className="w-3.5 h-3.5 animate-spin" />
                        <span className="text-xs">Loading preview…</span>
                      </div>
                    ) : previewText ? (
                      previewText
                    ) : (
                      <span className="text-gray-400 text-xs italic">
                        {metaBody === null && !metaLoading ? "Preview unavailable" : ""}
                      </span>
                    )}
                  </div>
                  {selected.footerText && (
                    <p className="text-[11px] text-gray-400 mt-1 px-1">{selected.footerText}</p>
                  )}
                </div>

                {/* Body params */}
                {params.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-1.5">Fill in variables</p>
                    <div className="space-y-2">
                      {params.map((val, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 w-8 flex-shrink-0">{`{{${i + 1}}}`}</span>
                          <input
                            value={val}
                            onChange={(e) => {
                              const next = [...params];
                              next[i] = e.target.value;
                              setParams(next);
                            }}
                            placeholder={`Variable ${i + 1}`}
                            className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Send button */}
              <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0">
                <button
                  onClick={handleSend}
                  disabled={sending}
                  className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-xl transition-colors"
                >
                  {sending ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Template
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            !loading && templates.length > 0 && (
              <div className="hidden sm:flex flex-1 items-center justify-center text-gray-400">
                <div className="text-center">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Select a template to preview</p>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePicker;
